import SEOHead from "@/components/layout/SEOHead";
import { PageHero } from "@/pages/About.hero";
import { Link } from "react-router-dom";
import { Music, BookOpen, Star, Heart } from "lucide-react";

const SERVICES = [
  { title: "Shabbat Evening Service", when: "Friday · 7:00 PM", desc: "Our weekly music-filled Shabbat service led by Rabbi Karen Fanwick. Oneg Shabbat reception follows.", icon: Music },
  { title: "Torah Study", when: "Every Saturday · 9:30 AM", desc: "Open, lively conversation about the weekly parashah with Rabbi Fanwick. All levels welcome - no prep required, drop in any week.", icon: BookOpen },
];

const LIFECYCLE = [
  { title: "B'nei Mitzvah", desc: "We work closely with each family to prepare their child for this meaningful rite of passage." },
  { title: "Conversion · A Journey of Discovery", desc: "Rabbi Fanwick's personalized program guides seekers gently and joyfully into Jewish life." },
  { title: "Weddings & Aufrufs", desc: "Beautiful, personalized ceremonies for our congregants and the broader community." },
  { title: "Baby Naming", desc: "Welcome your child into the Jewish covenant with love and song." },
  { title: "Funerals & Shiva Support", desc: "Compassionate ritual guidance in times of loss." },
];

const HOLIDAYS = ["Rosh Hashanah", "Yom Kippur", "Sukkot", "Simchat Torah", "Hanukkah", "Tu BiShvat", "Purim", "Passover Community Seder", "Shavuot", "Tisha B'Av"];

export default function Worship() {
  return (
    <>
      <SEOHead
        title="Worship at Our Synagogue in Melbourne FL"
        description="Music-filled Shabbat services, Torah study, and Jewish lifecycle events at Temple Israel of Brevard, the Reform synagogue in Melbourne, FL. Visitors welcome at every service."
        path="/worship"
      />
      <PageHero eyebrow="Worship & Services" title="Music-filled, heartfelt, and open to all." subtitle="Whether you're a longtime member, a curious seeker, or visiting Brevard, you'll find a warm welcome at every TIOB service." />

      <section className="bg-white py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-5 lg:px-10">
          <h2 className="font-display text-4xl md:text-5xl text-[var(--blue-ink)] mb-12">Weekly Services</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {SERVICES.map((s) => (
              <div key={s.title} className="card-soft p-7" data-testid={`service-${s.title.replace(/\s/g, "-").toLowerCase()}`}>
                <div className="w-14 h-14 rounded-2xl bg-[var(--blue)]/10 text-[var(--blue-deep)] flex items-center justify-center mb-5">
                  <s.icon size={24} />
                </div>
                <h3 className="font-display text-2xl text-[var(--blue-ink)]">{s.title}</h3>
                <p className="font-mono-accent text-xs uppercase tracking-widest text-[var(--blue-deep)] mt-2">{s.when}</p>
                <p className="mt-3 text-sm text-[var(--ink-soft)] leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[var(--cream)] py-20 lg:py-28 border-y border-[var(--line-warm)]">
        <div className="max-w-7xl mx-auto px-5 lg:px-10 grid lg:grid-cols-2 gap-12 items-start">
          <div>
            <p className="eyebrow mb-4">★ Lifecycle</p>
            <h2 className="font-display text-4xl md:text-5xl text-[var(--blue-ink)] leading-tight">
              Marking life's sacred moments - together.
            </h2>
            <p className="mt-6 text-[var(--ink-soft)] leading-relaxed text-lg">
              Rabbi Fanwick partners with each family to craft ceremonies that honor tradition,
              personality, and joy. Reach out - every conversation begins with listening.
            </p>
            <Link to="/contact" className="btn-primary mt-8" data-testid="worship-contact-cta">
              <Heart size={16} /> Speak with the rabbi
            </Link>
          </div>
          <div className="grid gap-3">
            {LIFECYCLE.map((l) => (
              <div key={l.title} className="bg-white rounded-2xl p-5 border border-[var(--line)] shadow-sm">
                <div className="flex items-start gap-3">
                  <Star size={18} className="text-[var(--honey)] fill-[var(--honey)] mt-1 shrink-0" />
                  <div>
                    <h3 className="font-display text-xl text-[var(--blue-ink)]">{l.title}</h3>
                    <p className="text-sm text-[var(--ink-soft)] mt-1">{l.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white py-20 lg:py-28">
        <div className="max-w-5xl mx-auto px-5 lg:px-10 text-center">
          <p className="eyebrow mb-4">★ Holidays & High Holy Days</p>
          <h2 className="font-display text-4xl md:text-5xl text-[var(--blue-ink)] leading-tight">
            The Jewish year, celebrated in full.
          </h2>
          <p className="mt-6 text-[var(--ink-soft)] max-w-2xl mx-auto leading-relaxed text-lg">
            From the sweetness of Rosh Hashanah to the joyous spirit of Purim - TIOB celebrates
            every season of the Jewish calendar. Visitors are warmly invited; please contact the
            office for High Holy Day tickets.
          </p>
          <div className="mt-10 flex flex-wrap justify-center gap-2.5">
            {HOLIDAYS.map((h) => (
              <span key={h} className="px-4 py-2 rounded-full bg-[var(--blue)]/8 text-[var(--blue-ink)] text-sm border border-[var(--blue)]/20 font-medium">
                {h}
              </span>
            ))}
          </div>
          <Link to="/events" className="btn-primary mt-10" data-testid="worship-events-cta">
            View calendar
          </Link>
        </div>
      </section>
    </>
  );
}
